import { createFileRoute } from '@tanstack/react-router'
import { useState, useRef, useEffect } from 'react'
import axios from 'axios'
import { FiChevronRight, FiAlertTriangle, FiCheckCircle, FiRefreshCw, FiChevronDown, FiPhone, FiGlobe } from 'react-icons/fi'
import { useStartSituation, useContinueSituation, useStepHelp, type SituationVerdict, type StepHelpResponse } from '@/hooks/useSituationAssessment'
import Breadcrumbs from '@/components/Breadcrumbs'

export const Route = createFileRoute('/_auth/tools/scam-check/situation')({
  validateSearch: (search: Record<string, unknown>) => ({
    q: typeof search.q === 'string' ? search.q : undefined,
    invitationcode: typeof search.invitationcode === 'string' ? search.invitationcode : undefined,
    __clerk_ticket: typeof search.__clerk_ticket === 'string' ? search.__clerk_ticket : undefined,
  }),
  component: SituationPage,
})

function VerdictCard({ verdict }: { verdict: SituationVerdict }) {
  const borderColor = verdict.risk === 'SAFE' ? 'border-l-emerald-500'
    : verdict.risk === 'CAUTION' ? 'border-l-amber-500'
    : 'border-l-red-500'

  const iconColor = verdict.risk === 'SAFE' ? 'text-emerald-500'
    : verdict.risk === 'CAUTION' ? 'text-amber-500'
    : 'text-red-500'

  const riskLabel = verdict.risk === 'SAFE' ? 'Low risk'
    : verdict.risk === 'CAUTION' ? 'Moderate risk'
    : 'High risk'

  const barColor = verdict.riskScore < 30 ? 'bg-emerald-500'
    : verdict.riskScore < 70 ? 'bg-amber-500'
    : 'bg-red-500'

  return (
    <div className={`rounded-2xl bg-white border-l-4 ${borderColor} shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_2px_4px_rgba(0,0,0,0.04)] p-6 sm:p-8`}>
      {/* Header */}
      <div className="flex items-start gap-4 mb-6">
        {verdict.risk === 'SAFE' ? (
          <FiCheckCircle className={`h-6 w-6 shrink-0 ${iconColor} mt-0.5`} />
        ) : (
          <FiAlertTriangle className={`h-6 w-6 shrink-0 ${iconColor} mt-0.5`} />
        )}
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-xl font-semibold text-slate-900">{verdict.headline}</h2>
            {verdict.scamType && (
              <span className="rounded-full bg-slate-100 px-3 py-0.5 text-sm font-medium text-slate-600">
                {verdict.scamType}
              </span>
            )}
          </div>
          <p className="mt-2 text-base leading-relaxed text-slate-600">{verdict.explanation}</p>
        </div>
      </div>

      {/* Risk score */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-700 ease-out ${barColor}`}
            style={{ width: `${verdict.riskScore}%` }}
          />
        </div>
        <span className="text-sm font-medium text-slate-500 tabular-nums shrink-0">{riskLabel} ({verdict.riskScore}/100)</span>
      </div>

      {/* Red flags */}
      {verdict.redFlags.length > 0 && (
        <div className="mb-6">
          <p className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-3">Warning signs</p>
          <div className="space-y-2">
            {verdict.redFlags.map((flag, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-300" />
                <p className="text-base text-slate-700">{flag}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Next steps — rendered by parent for interactivity */}
    </div>
  )
}

function SituationPage() {
  const searchParams = Route.useSearch()
  const initialQuery = searchParams.q || ''

  const startSituation = useStartSituation()
  const continueSituation = useContinueSituation()
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [verdict, setVerdict] = useState<SituationVerdict | null>(null)
  const [input, setInput] = useState('')
  const [currentQuestion, setCurrentQuestion] = useState<string | null>(null)
  const [step, setStep] = useState(0) // 0 = initial, 1+ = follow-ups
  const [hasAutoStarted, setHasAutoStarted] = useState(false)
  const [originalDescription, setOriginalDescription] = useState('')
  const [expandedStep, setExpandedStep] = useState<number | null>(null)
  const [stepHelpData, setStepHelpData] = useState<Record<number, StepHelpResponse>>({})
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const stepHelp = useStepHelp()
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const isLoading = startSituation.isPending || continueSituation.isPending

  // Auto-start if we arrived with a query
  useEffect(() => {
    if (initialQuery && !hasAutoStarted && !sessionId) {
      setHasAutoStarted(true)
      setStep(1)
      handleSubmit(initialQuery)
    }
  }, [initialQuery])

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value)
    const el = e.target
    el.style.height = 'auto'
    el.style.height = Math.min(el.scrollHeight, 150) + 'px'
  }

  const handleSubmit = async (text?: string) => {
    const value = (text || input).trim()
    if (!value || isLoading) return
    setInput('')
    setErrorMessage(null)
    if (textareaRef.current) textareaRef.current.style.height = 'auto'

    try {
      if (!sessionId) {
        setStep(1)
        setOriginalDescription(value)
        const result = await startSituation.mutateAsync(value)
        setSessionId(result.sessionId)
        if (result.status === 'verdict' && result.verdict) {
          setVerdict(result.verdict)
        } else {
          // Extract the assistant's question from messages
          const assistantMsg = result.messages.filter(m => m.role === 'assistant').pop()
          setCurrentQuestion(assistantMsg?.content || null)
          setStep(2)
        }
      } else {
        const result = await continueSituation.mutateAsync({ sessionId, reply: value })
        if (result.status === 'verdict' && result.verdict) {
          setVerdict(result.verdict)
        } else {
          const assistantMsg = result.messages.filter(m => m.role === 'assistant').pop()
          setCurrentQuestion(assistantMsg?.content || null)
          setStep((s) => s + 1)
        }
      }
    } catch (err) {
      // Never lose what they typed, and never leave a blank page
      const status = axios.isAxiosError(err) ? err.response?.status : undefined
      if (sessionId && status === 404) {
        // The conversation expired on the server — restart with their original description
        setSessionId(null)
        setCurrentQuestion(null)
        setStep(0)
        setInput(originalDescription || value)
        setErrorMessage("That conversation had expired, so we couldn't send your answer. Your description is back in the box — press \"Start assessment\" to begin a fresh check.")
      } else {
        setInput(value)
        if (!sessionId) setStep(0)
        setErrorMessage("Something went wrong sending your message. Your text is back in the box — please try again in a moment.")
      }
    }
  }

  const handleReset = () => {
    setSessionId(null)
    setVerdict(null)
    setInput('')
    setCurrentQuestion(null)
    setStep(0)
    setHasAutoStarted(false)
    setOriginalDescription('')
    setExpandedStep(null)
    setStepHelpData({})
    setErrorMessage(null)
  }

  const errorNotice = errorMessage && (
    <div className="mb-4 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
      <FiAlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
      <p className="text-sm text-amber-800">{errorMessage}</p>
    </div>
  )

  const handleStepHelp = async (stepIndex: number, stepText: string) => {
    if (stepHelpData[stepIndex]) {
      setExpandedStep(expandedStep === stepIndex ? null : stepIndex)
      return
    }
    setExpandedStep(stepIndex)
    const result = await stepHelp.mutateAsync({
      step: stepText,
      verdict: {
        headline: verdict!.headline,
        explanation: verdict!.explanation,
        scamType: verdict!.scamType,
        redFlags: verdict!.redFlags,
      },
      originalDescription,
    })
    setStepHelpData((prev) => ({ ...prev, [stepIndex]: result }))
  }

  return (
    <div className="min-h-screen bg-[#f5f5f7]">
      <div className="mx-auto max-w-3xl px-6 py-8 sm:px-8 sm:py-10">

        <Breadcrumbs
          items={[
            { label: 'Tools', href: '/tools' },
            { label: 'Situation Assessment' },
          ]}
        />

        {/* Header */}
        <header className="mt-4 mb-8">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
            Situation Assessment
          </h1>
          <p className="mt-1.5 text-base text-slate-500">
            {verdict ? 'Here\'s our assessment.' : 'Describe what happened and we\'ll help you figure out if it\'s a scam.'}
          </p>
        </header>

        {/* Loading state */}
        {isLoading && (
          <div className="rounded-2xl bg-white p-8 sm:p-10 shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_2px_4px_rgba(0,0,0,0.04)] text-center">
            <div className="flex justify-center mb-4">
              <div className="relative">
                <div className="h-12 w-12 rounded-full border-2 border-slate-100" />
                <div className="absolute inset-0 h-12 w-12 rounded-full border-2 border-violet-500 border-t-transparent animate-spin" />
              </div>
            </div>
            <p className="text-base font-medium text-slate-700">
              {step <= 1 ? 'Analysing your situation...' : 'Reviewing your answer...'}
            </p>
            <p className="mt-1 text-sm text-slate-400">
              This usually takes a few seconds.
            </p>
          </div>
        )}

        {/* Step 0: Initial input */}
        {!isLoading && !verdict && step === 0 && (
          <div className="space-y-6">
            <div className="rounded-2xl bg-white p-6 shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_2px_4px_rgba(0,0,0,0.04)] sm:p-8">
              {errorNotice}
              <label className="block text-base font-medium text-slate-700 mb-3">
                What's going on?
              </label>
              <textarea
                ref={textareaRef}
                value={input}
                onChange={handleTextareaChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && input.trim()) {
                    e.preventDefault()
                    handleSubmit()
                  }
                }}
                placeholder="Describe what happened in your own words..."
                className="w-full resize-none rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-base text-slate-900 placeholder-slate-400 outline-none transition-colors focus:border-violet-400 focus:ring-2 focus:ring-violet-100"
                style={{ minHeight: '100px', maxHeight: '150px' }}
                autoFocus
              />
              <p className="mt-2 text-sm text-slate-400">
                No technical knowledge needed. Just tell us what happened.
              </p>

              <div className="mt-5 flex justify-end">
                <button
                  type="button"
                  onClick={() => handleSubmit()}
                  disabled={!input.trim()}
                  className={`inline-flex items-center gap-2 rounded-xl px-6 py-3 text-base font-medium transition-colors disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed cursor-pointer ${
                    input.trim() ? 'bg-violet-600 text-white hover:bg-violet-700' : ''
                  }`}
                >
                  Start assessment
                  <FiChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Example prompts */}
            <div className="rounded-2xl bg-white p-6 shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_2px_4px_rgba(0,0,0,0.04)] sm:p-8">
              <p className="text-sm font-medium text-slate-700 mb-3">Not sure how to describe it? Try one of these:</p>
              <div className="space-y-2">
                {[
                  "Someone called saying they're from my bank and asked me to move money to a 'safe account'",
                  "I got a text saying I have an undelivered parcel and need to pay a fee",
                  "A person I met online is asking me to invest in cryptocurrency",
                  "I received an email saying my account will be closed unless I verify my details",
                ].map((example) => (
                  <button
                    key={example}
                    type="button"
                    onClick={() => { setInput(example); textareaRef.current?.focus() }}
                    className="block w-full text-left rounded-xl border border-slate-200/60 px-4 py-3 text-sm text-slate-600 transition-colors hover:border-violet-300 hover:bg-violet-50 hover:text-violet-700 cursor-pointer"
                  >
                    "{example}"
                  </button>
                ))}
              </div>
            </div>

            {/* How it works */}
            <div className="rounded-2xl bg-white p-6 shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_2px_4px_rgba(0,0,0,0.04)] sm:p-8">
              <h3 className="text-base font-semibold text-slate-900 mb-4">How this works</h3>
              <div className="space-y-3">
                {[
                  'Describe the situation in your own words',
                  "We'll ask a few short questions to understand what happened",
                  "You'll get a clear verdict with advice on what to do next",
                ].map((text, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-violet-50 text-xs font-semibold text-violet-700">
                      {i + 1}
                    </div>
                    <p className="text-sm text-slate-600 pt-0.5">{text}</p>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-sm text-slate-400">
                Your conversation is private and not shared with anyone.
              </p>
            </div>
          </div>
        )}

        {/* Follow-up question step */}
        {!isLoading && !verdict && step >= 2 && currentQuestion && (
          <div className="rounded-2xl bg-white p-6 shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_2px_4px_rgba(0,0,0,0.04)] sm:p-8">
            {/* Progress */}
            <div className="flex items-center gap-2 mb-6">
              <span className="text-xs font-medium text-slate-400">Question {step - 1}</span>
              <div className="flex-1 h-1 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className="h-full rounded-full bg-violet-500 transition-all duration-300"
                  style={{ width: `${Math.min(100, (step - 1) * 25)}%` }}
                />
              </div>
            </div>

            {/* Question */}
            <p className="text-lg text-slate-800 leading-relaxed mb-5">
              {currentQuestion}
            </p>

            {errorNotice}

            {/* Answer input */}
            <textarea
              ref={textareaRef}
              value={input}
              onChange={handleTextareaChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && input.trim()) {
                  e.preventDefault()
                  handleSubmit()
                }
              }}
              placeholder="Type your answer..."
              className="w-full resize-none rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-base text-slate-900 placeholder-slate-400 outline-none transition-colors focus:border-violet-400 focus:ring-2 focus:ring-violet-100"
              style={{ minHeight: '60px', maxHeight: '120px' }}
              autoFocus
            />

            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => handleSubmit()}
                disabled={!input.trim()}
                className={`inline-flex items-center gap-2 rounded-xl px-6 py-3 text-base font-medium transition-colors disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed cursor-pointer ${
                  input.trim() ? 'bg-violet-600 text-white hover:bg-violet-700' : ''
                }`}
              >
                Continue
                <FiChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* Verdict */}
        {!isLoading && verdict && (
          <div className="space-y-5">
            <VerdictCard verdict={verdict} />

            {/* Interactive next steps */}
            {verdict.nextSteps.length > 0 && (
              <div className="rounded-2xl bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_2px_4px_rgba(0,0,0,0.04)] p-6 sm:p-8">
                <p className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-4">What to do next</p>
                <div className="space-y-3">
                  {verdict.nextSteps.map((stepText, i) => {
                    const isExpanded = expandedStep === i
                    const helpData = stepHelpData[i]
                    const isLoadingHelp = stepHelp.isPending && expandedStep === i && !helpData

                    return (
                      <div key={i} className="rounded-xl border border-slate-200/60 overflow-hidden">
                        {/* Step header */}
                        <div className="flex items-start gap-3 p-4">
                          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-violet-50 text-xs font-semibold text-violet-700 mt-0.5">
                            {i + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-base text-slate-700">{stepText}</p>
                            <button
                              type="button"
                              onClick={() => handleStepHelp(i, stepText)}
                              className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-violet-600 transition-colors hover:text-violet-700 cursor-pointer"
                            >
                              {isLoadingHelp ? (
                                <>
                                  <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-violet-200 border-t-violet-600" />
                                  Getting details...
                                </>
                              ) : isExpanded && helpData ? (
                                <>
                                  <FiChevronDown className="h-3.5 w-3.5 rotate-180 transition-transform" />
                                  Hide details
                                </>
                              ) : (
                                <>
                                  <FiChevronDown className="h-3.5 w-3.5" />
                                  Help me with this
                                </>
                              )}
                            </button>
                          </div>
                        </div>

                        {/* Expanded help */}
                        {isExpanded && helpData && (
                          <div className="border-t border-slate-100 bg-slate-50/50 p-4 pl-[52px] space-y-4">
                            {/* Detailed instructions */}
                            <div className="space-y-2">
                              {helpData.instructions.map((instruction, j) => (
                                <div key={j} className="flex items-start gap-2.5">
                                  <span className="text-xs font-medium text-slate-400 mt-0.5 shrink-0 w-4 text-right">{j + 1}.</span>
                                  <p className="text-sm text-slate-700 leading-relaxed">{instruction}</p>
                                </div>
                              ))}
                            </div>

                            {/* Contact info */}
                            {helpData.contactInfo && (
                              <div className="rounded-xl bg-white border border-slate-200/60 p-4">
                                <p className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-2">Official contact</p>
                                <p className="text-base font-semibold text-slate-900">{helpData.contactInfo.organisation}</p>
                                <div className="flex flex-wrap items-center gap-4 mt-2">
                                  {helpData.contactInfo.phone && (
                                    <a
                                      href={`tel:${helpData.contactInfo.phone.replace(/\s/g, '')}`}
                                      className="inline-flex items-center gap-1.5 text-sm font-medium text-violet-700 hover:text-violet-800"
                                    >
                                      <FiPhone className="h-3.5 w-3.5" />
                                      {helpData.contactInfo.phone}
                                    </a>
                                  )}
                                  {helpData.contactInfo.website && (
                                    <a
                                      href={helpData.contactInfo.website}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1.5 text-sm font-medium text-violet-700 hover:text-violet-800"
                                    >
                                      <FiGlobe className="h-3.5 w-3.5" />
                                      Official website
                                    </a>
                                  )}
                                </div>
                                <p className="mt-2 text-xs text-slate-400">{helpData.contactInfo.note}</p>
                              </div>
                            )}

                            {/* Warning */}
                            {helpData.warning && (
                              <div className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2.5">
                                <FiAlertTriangle className="h-4 w-4 shrink-0 text-amber-500 mt-0.5" />
                                <p className="text-sm text-amber-800">{helpData.warning}</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={handleReset}
                className="inline-flex items-center gap-2 text-sm font-medium text-slate-400 transition-colors hover:text-slate-600 cursor-pointer"
              >
                <FiRefreshCw className="h-3.5 w-3.5" />
                Check another situation
              </button>
              <a
                href={(() => {
                  const parts = ['I used the Situation Assessment tool and would like expert advice.'];
                  if (originalDescription) parts.push(`\nMy description: ${originalDescription.slice(0, 300)}`);
                  if (verdict) {
                    parts.push(`\nAssessment: ${verdict.headline}`);
                    parts.push(`Risk: ${verdict.risk} (${verdict.riskScore}/100)`);
                    if (verdict.scamType) parts.push(`Type: ${verdict.scamType}`);
                  }
                  parts.push('\n\n--- Add any additional details below ---\n');
                  return `/contact-support?topic=suspicious&message=${encodeURIComponent(parts.join('\n'))}`;
                })()}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200/60 px-3 py-1.5 text-sm font-medium text-slate-500 transition-colors hover:border-violet-300 hover:bg-violet-50 hover:text-violet-700"
              >
                Talk to an expert
              </a>
            </div>
          </div>
        )}

      </div>
    </div>
  )
}
